import * as d3 from "d3";
import axios from 'axios';

export default class MitMapLegendService {
    constructor(svgSelector) {
        this.svgSelector = svgSelector;
        this.svg = d3.select(svgSelector);
        this.layers = {}; 
        
        // Dictionary to track { "floorId_legendType": { x, y, width, height } }
        this.modifications = {}; 
        // State tracking so it doesn't snap back before saving
        this.activeSettings = {};

        // this.CONFERENCE_COLOR = '#000000';
        // // this.CONFERENCE_COLOR_ROOM_TYPE = "#1976D2";
        // this.CONFERENCE_COLOR_ROOM_TYPE = "#5e5d5d";
        // //this.CONFERENCE_COLOR_ROOM_TYPE = "rgb(47, 64, 111)";
        // this.dimNeighborhoodsForConference = false;

        this.CONFERENCE_COLOR = '#cbcaca';
        this.CONFERENCE_COLOR_BORDER = '#cbcaca';
        this.CONFERENCE_COLOR_ROOM_TYPE = "#5e5d5d";
        this.dimNeighborhoodsForConference = false;

        // Matches MitMapService.SELECTION_COLOR
        this.SELECTION_COLOR = '#1F3A68';
        this.ROW_ACTIVE_BG = 'rgba(31, 58, 104, 0.12)';
        this.ROW_HOVER_BG = 'rgba(31, 58, 104, 0.06)';
    }

    recordModification(floorId, legendType, settings) {
        const key = `${floorId}_${legendType}`;
        this.modifications[key] = {
            x: settings.x,
            y: settings.y,
            width: settings.width,
            height: settings.height
        };
        this.activeSettings[key] = this.modifications[key];
    }

    async saveAllModifications(buildingId) {
        const keys = Object.keys(this.modifications);
        if (keys.length === 0) return;

        let savePromises = keys.map(key => {
            const [floorId, legendType] = key.split('_');
            const settings = this.modifications[key];
            return this.saveSettings(buildingId, floorId, legendType, settings);
        });

        await Promise.allSettled(savePromises);
        this.clearModifications();
    }

    clearModifications() {
        this.modifications = {};
    }

    async loadSettings(buildingId, floorId, legendType = 'neighborhood') {
        const key = `${floorId}_${legendType}`;
        if (this.activeSettings[key]) return this.activeSettings[key];

        try {
            const res = await axios.get(`/api/v2/user/spaces/buildings/${buildingId}/floors/${floorId}/legend?type=${legendType}`);
            if (res.data && res.data.x !== undefined) {
                this.activeSettings[key] = res.data;
                return res.data;
            }
        } catch (err) {
            console.warn(`Legend settings not found for ${legendType}, using defaults.`);
        }
        
        const defaultSettings = { x: 20, y: 20, width: 220, height: "auto" };
        this.activeSettings[key] = defaultSettings;
        return defaultSettings;
    }

    async saveSettings(buildingId, floorId, legendType, settingsPayload) {
        try {
            await axios.post(`/api/v2/user/spaces/buildings/${buildingId}/floors/${floorId}/legend?type=${legendType}`, settingsPayload);
        } catch (err) {
            console.error("Failed to save legend settings.");
        }
    }

    clearLegend() {
        Object.values(this.layers).forEach(layer => {
            if (layer) layer.remove();
        });
        this.layers = {};
    }

    // interaction: { onSelect(legendType, item), onHover(legendType, item|null), activeGroupId, activeSpaceId }
    renderLegend(groups, currentFloorSpaces, editMode, buildingId, floorId, showNeighborhoods, showConferenceRooms, interaction = {}) {
        this.clearLegend();
        if (!currentFloorSpaces || !currentFloorSpaces.features) return;

        if (showNeighborhoods) {
            const floorSpaceIds = new Set(currentFloorSpaces.features.map(f => f.properties.mitId));
            const activeGroups = groups.filter(g => {
                if (!g.spaceIds) return false;
                return g.spaceIds.some(id => floorSpaceIds.has(id));
            });
            const settings = this.activeSettings[`${floorId}_neighborhood`];
            if (settings) this.drawLegendBox('neighborhood', 'Groups', activeGroups, editMode, floorId, settings, interaction);
        }

        if (showConferenceRooms) {
            const confItems = currentFloorSpaces.features
                .filter(f => {
                    const useType = f.properties && f.properties.useType ? f.properties.useType.toUpperCase() : '';
                    return useType === 'CONFERENCE ROOM' || useType === 'SEMINAR ROOM';
                })
                .map(f => ({
                    useType: f.properties.useType || 'Conference Room',
                    name: f.properties.name || '',
                    mitId: f.properties.mitId || '',
                    // color: '#8E44AD' // Matching purple accent
                    // color: this.CONFERENCE_COLOR
                    // color: this.CONFERENCE_COLOR,
                    color: this.dimNeighborhoodsForConference ? this.CONFERENCE_COLOR : 'url(#conf-hatch)',
                    isPattern: !this.dimNeighborhoodsForConference
                    // isSquare: true
                }));

            const settings = this.activeSettings[`${floorId}_conference`];
            if (settings) {
                this.drawLegendBox('conference', 'Meeting Spaces', confItems, editMode, floorId, settings, interaction);
            }
        }
    }

    isActiveItem(legendType, item, interaction) {
        if (legendType === 'conference') return !!interaction.activeSpaceId && item.mitId === interaction.activeSpaceId;
        return interaction.activeGroupId != null && item.id === interaction.activeGroupId;
    }

    drawLegendBox(legendType, title, items, editMode, floorId, settings, interaction = {}) {
        this.svg = d3.select(this.svgSelector);
        let mapGroup = this.svg.select("#group-map");
        if (mapGroup.empty()) mapGroup = this.svg;
        
        const MAX_ROWS = 8;
        const COLUMN_WIDTH = legendType === 'conference' ? 260 : 180;
        const ROW_HEIGHT = 30;
        
        const requiredColumns = Math.ceil(items.length / MAX_ROWS) || 1;
        const dynamicWidth = Math.max(settings.width || 220, requiredColumns * COLUMN_WIDTH + 20);

        this.layers[legendType] = mapGroup.append("g")
            .attr("id", `group-legend-${legendType}`)
            .attr("transform", `translate(${settings.x}, ${settings.y})`)
            .style("user-select", "none");

        const minHeight = items.length === 0 ? 80 : (Math.min(items.length, MAX_ROWS) * ROW_HEIGHT) + 65;
        let parsedHeight = (settings.height === 'auto' || !settings.height) ? minHeight : parseFloat(settings.height);
        const boxHeight = Math.max(parsedHeight, minHeight);
        
        const bgRect = this.layers[legendType].append("rect")
            .attr("width", dynamicWidth)
            .attr("height", boxHeight)
            .attr("fill", "rgba(255, 255, 255, 0.96)")
            .attr("rx", 10)
            .style("pointer-events", "all");

        if (!editMode) {
            this.layers[legendType].on("mousedown", (event) => event.stopPropagation());
            this.layers[legendType].on("touchstart", (event) => event.stopPropagation());
        }

        this.layers[legendType].append("text")
            .attr("x", 16).attr("y", 26)
            .attr("font-family", "system-ui, -apple-system, sans-serif")
            .attr("font-weight", "600")
            .attr("font-size", "14px")
            .attr("letter-spacing", "0.5px")
            .attr("fill", "#111827")
            .text(title);

        this.layers[legendType].append("line")
            .attr("x1", 0).attr("y1", 40)
            .attr("x2", dynamicWidth).attr("y2", 40)
            .attr("stroke", "#f3f4f6")
            .attr("stroke-width", "1.5px");

        const itemsGroup = this.layers[legendType].append("g").attr("transform", "translate(16, 56)");
        
        if (items.length === 0) {
            itemsGroup.append("text")
                .attr("x", 0).attr("y", 12)
                .attr("font-size", "13px")
                .attr("fill", "#9ca3af")
                .attr("font-style", "italic")
                .text("No items displayed here.");
        } else {
            items.forEach((item, i) => {
                const col = Math.floor(i / MAX_ROWS);
                const rowIdx = i % MAX_ROWS;
                
                const row = itemsGroup.append("g")
                    .attr("transform", `translate(${col * COLUMN_WIDTH}, ${rowIdx * ROW_HEIGHT})`);

                const interactive = !editMode && typeof interaction.onSelect === 'function';
                const isActive = this.isActiveItem(legendType, item, interaction);
                const restBg = isActive ? this.ROW_ACTIVE_BG : 'transparent';

                // Row background doubles as the click/hover target
                const rowBg = row.append("rect")
                    .attr("x", -8).attr("y", -8)
                    .attr("width", COLUMN_WIDTH - 12).attr("height", ROW_HEIGHT - 2)
                    .attr("rx", 6)
                    .attr("fill", restBg);

                if (isActive) {
                    row.append("rect")
                        .attr("x", -8).attr("y", -8)
                        .attr("width", 3).attr("height", ROW_HEIGHT - 2)
                        .attr("rx", 1.5)
                        .attr("fill", this.SELECTION_COLOR);
                }

                if (interactive) {
                    row.style("cursor", "pointer")
                        .on("click", (event) => {
                            event.stopPropagation();
                            interaction.onSelect(legendType, item);
                        })
                        .on("mouseenter", () => {
                            if (!isActive) rowBg.attr("fill", this.ROW_HOVER_BG);
                            if (interaction.onHover) interaction.onHover(legendType, item);
                        })
                        .on("mouseleave", () => {
                            rowBg.attr("fill", restBg);
                            if (interaction.onHover) interaction.onHover(legendType, null);
                        });
                }

                // Circle swatch
                // row.append("circle")
                //     .attr("cx", 6).attr("cy", 6)
                //     .attr("r", 6)
                //     .attr("fill", item.color || "#cccccc")
                //     .attr("stroke", item.isPattern ? this.CONFERENCE_COLOR : "#ffffff")
                //     .attr("stroke-width", "1.5px");

                // if (item.isSquare) {
                //     // Draw a solid square with a 1px border matching the map
                //     row.append("rect")
                //         .attr("x", 0).attr("y", 0)
                //         .attr("width", 12).attr("height", 12)
                //         .attr("fill", item.color)
                //         .attr("stroke", this.CONFERENCE_COLOR_BORDER)
                //         // .attr("stroke", d3.color(item.color).darker(1.5).formatHex())
                //         .attr("stroke-width", "1px"); 
                // } else {
                //     // Standard Circle swatch for solid neighborhood colors
                //     row.append("circle")
                //         .attr("cx", 6).attr("cy", 6)
                //         .attr("r", 6)
                //         .attr("fill", item.color || "#cccccc")
                //         .attr("stroke", "#ffffff")
                //         .attr("stroke-width", "1.5px");
                // }
                if (item.isPattern) {
                    // Draw a square with no border to better represent a physical room on the map
                    row.append("rect")
                        .attr("x", 0).attr("y", 0)
                        .attr("width", 12).attr("height", 12)
                        .attr("fill", item.color)
                        .attr("stroke", "none"); // Removes the outside border
                } else {
                    // Standard Circle swatch for solid neighborhood colors
                    row.append("circle")
                        .attr("cx", 6).attr("cy", 6)
                        .attr("r", 6)
                        .attr("fill", item.color || "#cccccc")
                        .attr("stroke", "#ffffff")
                        .attr("stroke-width", "1.5px");
                }

                // Formatted multi-part text line
                const textNode = row.append("text")
                    .attr("x", 22).attr("y", 11)
                    .attr("font-family", "system-ui, -apple-system, sans-serif")
                    .attr("font-size", "12px");

                if (legendType === 'conference') {
                    // 1. Type in Primary Blue
                    textNode.append("tspan")
                        .attr("fill", this.CONFERENCE_COLOR_ROOM_TYPE)
                        .attr("font-weight", "300")
                        .text(item.useType);

                    // 2. Name (if it exists)
                    if (item.name) {
                        textNode.append("tspan")
                            .attr("fill", "#111827")
                            .attr("font-weight", isActive ? "700" : "500")
                            .text(` | ${item.name}`);

                         // 3. Full mitId in softer grey
                        textNode.append("tspan")
                            .attr("fill", "#4d525c")
                            .attr("font-weight", "400")
                            .text(` ${item.mitId}`);
                    }
                    else{
                        // 3. Full mitId in softer grey
                        textNode.append("tspan")
                            .attr("fill", "#111827")
                            .attr("font-weight", isActive ? "700" : "500")
                            .text(` | ${item.mitId}`);
                    }

                   
                } else {
                    // Standard Neighborhood rendering fallback
                    textNode.append("tspan")
                        .attr("fill", isActive ? "#111827" : "#4b5563")
                        .attr("font-weight", isActive ? "700" : "500")
                        .text(item.name || "Unnamed");
                }
            });
        }

        this.layers[legendType].raise();

        if (editMode) {
            this.layers[legendType].attr("cursor", "move");

            const drag = d3.drag()
                .on("start", function() { d3.select(this).raise(); })
                .on("drag", (event) => {
                    settings.x += event.dx;
                    settings.y += event.dy;
                    this.layers[legendType].attr("transform", `translate(${settings.x}, ${settings.y})`);
                })
                .on("end", () => {
                    this.recordModification(floorId, legendType, settings);
                });
            this.layers[legendType].call(drag);

            const handleSize = 20;
            const resizeHandle = this.layers[legendType].append("rect")
                .attr("class", "legend-resize-handle")
                .attr("x", dynamicWidth - handleSize)
                .attr("y", boxHeight - handleSize)
                .attr("width", handleSize)
                .attr("height", handleSize)
                .attr("fill", "transparent")
                .attr("cursor", "nwse-resize");

            const resize = d3.drag()
                .on("drag", (event) => {
                    settings.width = Math.max(160, (settings.width || dynamicWidth) + event.dx);
                    let newHeight = Math.max(minHeight, parseFloat(bgRect.attr("height")) + event.dy);
                    settings.height = newHeight;
                    bgRect.attr("width", settings.width).attr("height", newHeight);
                    
                    this.layers[legendType].select("line").attr("x2", settings.width);
                    resizeHandle.attr("x", settings.width - handleSize).attr("y", newHeight - handleSize);
                })
                .on("end", () => {
                    this.recordModification(floorId, legendType, settings);
                });
            
            resizeHandle.call(resize);
            resizeHandle.on("mousedown", (event) => event.stopPropagation());
        }
    }
}